import { useState, useEffect, useCallback } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonTitle,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonBadge,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonText,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  RefresherEventDetail,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import { useAuth } from '@clerk/clerk-react';
import { usePageView } from '../hooks/useLoggerInit';
import {
  logInOutline,
  logOutOutline,
  eyeOutline,
  personOutline,
  timeOutline,
  refreshOutline,
  alertCircleOutline,
  downloadOutline,
  cloudUploadOutline,
} from 'ionicons/icons';
import { useI18n } from '../i18n/I18nProvider';

interface ActivityLog {
  id: string;
  userId: string;
  eventType: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

interface LogsResponse {
  data: ActivityLog[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

import { API_BASE_URL, apiHeaders } from '../lib/api';

const ActivityLogsPage: React.FC = () => {
  // Track page view
  usePageView('activity_logs');

  const { t } = useI18n();

  // Event type icons and colors
  const eventConfig: Record<string, { icon: string; color: string; label: string }> =
    {
      user_signed_in: {
        icon: logInOutline,
        color: 'success',
        label: t('activity.event.signedIn'),
      },
      user_signed_out: {
        icon: logOutOutline,
        color: 'warning',
        label: t('activity.event.signedOut'),
      },
      user_session_active: {
        icon: personOutline,
        color: 'primary',
        label: t('activity.event.sessionActive'),
      },
      page_view: {
        icon: eyeOutline,
        color: 'secondary',
        label: t('activity.event.pageView'),
      },
      user_registered: {
        icon: personOutline,
        color: 'success',
        label: t('activity.event.userRegistered'),
      },
      user_updated: {
        icon: refreshOutline,
        color: 'tertiary',
        label: t('activity.event.userUpdated'),
      },
      data_export_requested: {
        icon: downloadOutline,
        color: 'primary',
        label: t('activity.event.dataExportRequested'),
      },
      data_import_applied: {
        icon: cloudUploadOutline,
        color: 'tertiary',
        label: t('activity.event.dataImportApplied'),
      },
      default: {
        icon: timeOutline,
        color: 'medium',
        label: t('activity.event.default'),
      },
    };

  const { getToken, userId } = useAuth();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const limit = 20;

  const fetchLogs = useCallback(async (reset = false) => {
    try {
      setError(null);
      if (reset) {
        setLoading(true);
        setOffset(0);
      }

      const currentOffset = reset ? 0 : offset;
      const token = await getToken();
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
      });

      if (userId) {
        params.append('userId', userId);
      }

      if (filter !== 'all') {
        params.append('eventType', filter);
      }

      const response = await fetch(`${API_BASE_URL}/api/logs?${params}`, {
        headers: apiHeaders(token),
      });

      if (!response.ok) {
        throw new Error(t('activity.error.fetchFailed'));
      }

      const data: LogsResponse = await response.json();
      
      if (reset) {
        setLogs(data.data);
      } else {
        setLogs(prev => [...prev, ...data.data]);
      }

      setHasMore(data.data.length === limit);
      setOffset(currentOffset + data.data.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('activity.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [getToken, userId, offset, filter, t]);

  useEffect(() => {
    fetchLogs(true);
  }, [filter]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await fetchLogs(true);
    event.detail.complete();
  };

  const loadMore = async (event: CustomEvent<void>) => {
    await fetchLogs(false);
    (event.target as HTMLIonInfiniteScrollElement).complete();
  };

  const getEventConfig = (eventType: string) => {
    return eventConfig[eventType] || eventConfig.default;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('activity.time.justNow');
    if (diffMins < 60) return t('activity.time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('activity.time.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('activity.time.daysAgo', { count: diffDays });
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMetadataDisplay = (log: ActivityLog) => {
    if (log.eventType === 'page_view' && log.metadata?.page) {
      return t('activity.metadata.page', {
        page: String(log.metadata.page),
      });
    }
    if (log.eventType === 'data_export_requested' && log.metadata) {
      const fmt = log.metadata.format != null ? String(log.metadata.format) : '';
      const bytes = log.metadata.byteLength != null ? String(log.metadata.byteLength) : '';
      if (fmt || bytes) {
        return [fmt && `format: ${fmt}`, bytes && `${bytes} bytes`]
          .filter(Boolean)
          .join(' · ');
      }
    }
    if (log.eventType === 'data_import_applied' && log.metadata) {
      const c = log.metadata.contactsCreated;
      const k = log.metadata.keysImported;
      if (c != null || k != null) {
        return `contacts: ${String(c ?? 0)}, keys: ${String(k ?? 0)}`;
      }
    }
    return null;
  };

  const filteredLogs = logs.filter(log => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      log.eventType.toLowerCase().includes(searchLower) ||
      getEventConfig(log.eventType).label.toLowerCase().includes(searchLower)
    );
  });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref="/dashboard"
              text={t('common.actions.back')}
            />
          </IonButtons>
          <IonTitle>{t('activity.title')}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            value={searchText}
            onIonInput={e => setSearchText(e.detail.value || '')}
            placeholder={t('activity.search.placeholder')}
            debounce={300}
          />
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={filter} onIonChange={e => setFilter(e.detail.value as string)}>
            <IonSegmentButton value="all">
              <IonLabel>{t('activity.filter.all')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="page_view">
              <IonLabel>{t('activity.filter.views')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="user_signed_in">
              <IonLabel>{t('activity.filter.signIn')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {loading && logs.length === 0 ? (
          <div className="loading-container">
            <IonSpinner name="crescent" />
            <IonText color="medium">
              <p>{t('activity.loading')}</p>
            </IonText>
          </div>
        ) : error ? (
          <div className="error-container">
            <IonIcon icon={alertCircleOutline} color="danger" />
            <IonText color="danger">
              <p>{error}</p>
            </IonText>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="empty-container">
            <IonIcon icon={timeOutline} color="medium" />
            <IonText color="medium">
              <p>{t('activity.empty')}</p>
            </IonText>
          </div>
        ) : (
          <IonList>
            {filteredLogs.map(log => {
              const config = getEventConfig(log.eventType);
              const metadataDisplay = getMetadataDisplay(log);
              
              return (
                <IonItem key={log.id} lines="full">
                  <IonIcon
                    icon={config.icon}
                    slot="start"
                    color={config.color}
                    className="activity-icon"
                  />
                  <IonLabel>
                    <h2>{config.label}</h2>
                    {metadataDisplay && (
                      <p className="metadata">{metadataDisplay}</p>
                    )}
                    <p className="timestamp">{formatDate(log.createdAt)}</p>
                  </IonLabel>
                  <IonBadge slot="end" color={config.color}>
                    {log.eventType.split('_').pop()}
                  </IonBadge>
                </IonItem>
              );
            })}
          </IonList>
        )}

        <IonInfiniteScroll
          onIonInfinite={loadMore}
          threshold="100px"
          disabled={!hasMore || loading}
        >
          <IonInfiniteScrollContent
            loadingSpinner="crescent"
            loadingText={t('activity.loadingMore')}
          />
        </IonInfiniteScroll>

        <style>{`
          .loading-container,
          .error-container,
          .empty-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 50vh;
            gap: 16px;
          }

          .loading-container ion-spinner {
            width: 48px;
            height: 48px;
          }

          .error-container ion-icon,
          .empty-container ion-icon {
            font-size: 64px;
          }

          .activity-icon {
            font-size: 24px;
          }

          ion-item h2 {
            font-size: 16px;
            font-weight: 600;
          }

          ion-item .metadata {
            font-size: 14px;
            color: var(--ion-color-medium);
          }

          ion-item .timestamp {
            font-size: 12px;
            color: var(--ion-color-medium-shade);
          }

          ion-badge {
            font-size: 10px;
            text-transform: capitalize;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default ActivityLogsPage;

